import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { OperatorOrganization } from '@/features/auth/auth-model'
import { OrganizationCompanyDataCell } from '@/features/organization-settings/components/organization-company-data-cell'
import { getBusinessRegistrationLabelTranslationKey } from '@/features/organization-settings/lib/business-registration-label'

type OrganizationInfoReadonlyCardProps = {
  organization: OperatorOrganization
  doesOrganizationRequiresLocalAddress: boolean
  onCertificateClick: () => void
}

export function OrganizationInfoReadonlyCard({
  organization,
  doesOrganizationRequiresLocalAddress,
  onCertificateClick,
}: OrganizationInfoReadonlyCardProps) {
  const { t } = useTranslation('settings')
  const placeOfRegistration = organization.placeOfRegistration ?? ''
  const registrationLabelCode =
    placeOfRegistration || organization.country || ''
  const businessRegLabelKey =
    getBusinessRegistrationLabelTranslationKey(registrationLabelCode)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Info className="size-5 shrink-0" />
          <CardTitle>{t('organizationDetails.organizationInfo.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className={`grid gap-4 ${doesOrganizationRequiresLocalAddress ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'}`}
        >
          <OrganizationCompanyDataCell
            label={
              doesOrganizationRequiresLocalAddress ? (
                <span className="inline-flex items-center gap-1">
                  {t('organizationDetails.organizationInfo.fields.name')}
                  <ReactCountryFlag countryCode={placeOfRegistration} svg />
                </span>
              ) : (
                t('organizationDetails.organizationInfo.fields.name')
              )
            }
          >
            {doesOrganizationRequiresLocalAddress
              ? organization.localName
              : organization.name}
          </OrganizationCompanyDataCell>
          {doesOrganizationRequiresLocalAddress ? (
            <OrganizationCompanyDataCell
              label={
                <span className="inline-flex items-center gap-1">
                  {t('organizationDetails.organizationInfo.fields.name')}
                  <ReactCountryFlag countryCode="gb" svg />
                </span>
              }
            >
              {organization.name}
            </OrganizationCompanyDataCell>
          ) : null}
          <OrganizationCompanyDataCell label={t(businessRegLabelKey)}>
            {organization.businessRegistrationNumber || '—'}
          </OrganizationCompanyDataCell>
          {organization.certificateFileName ? (
            <OrganizationCompanyDataCell
              label={t(
                'organizationDetails.organizationInfo.fields.businessCertificate'
              )}
              interactive
              onActivate={onCertificateClick}
            >
              {t('organizationDetails.organizationInfo.fields.viewCertificate')}
            </OrganizationCompanyDataCell>
          ) : (
            <OrganizationCompanyDataCell
              label={t(
                'organizationDetails.organizationInfo.fields.businessCertificate'
              )}
            >
              —
            </OrganizationCompanyDataCell>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
